"use client";

import useSWR from "swr";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faFileImage, faImage} from "@fortawesome/pro-duotone-svg-icons";
import Grid from "@mui/material/Grid2";
import {Box} from "@mui/material";
import JournalCoverImage from "@/src/components/journal-site/common/journal-cover-image/journal-cover-image";
import SuccessButton from "@/src/mui-custom/button/button-success";
import CustomDivider from "@/src/mui-custom/custom-divider/custom-divider";
import PageContentBox
    from "@/src/components/journal-site/common/page-content/mui-custom/page-content-box/page-content-box";
import ArchiveSelector from "@/src/components/journal-site/archive/archive-selector";
import SocialIcons from "@/src/components/common/pages/social-icons/social-icons";
import ArchiveIssue from "@/src/_types/archive-issue";
import Volume from "@/src/_types/volume";
import Archive from "@/src/_types/Archive/archive";
import {MapToArchiveIssueItems, MapToArchiveItems} from "@/src/utils/database-helpers/archive-helper";
import {MapToVolumeItems} from "@/src/utils/database-helpers/volume-helper";
import {fetcher} from "@/src/utils/fetch-helpers";
import Loading from "@/src/components/common/loading/loading";

export default function ArchivePage({initialJournalData, initialIssuesData, journalTitle}: {
    initialJournalData: any;
    initialIssuesData: any;
    journalTitle: string;
}) {
    const {
        data: journalResponse,
        isLoading: isJournalLoading
    } = useSWR(`/api/journals?path=${journalTitle}`, fetcher, {
        fallbackData: {success: true, data: [initialJournalData]},
        revalidateOnMount: false,
        revalidateOnFocus: false,
        revalidateIfStale: false,
        revalidateOnReconnect: false,
    });

    const journal = Array.isArray(journalResponse?.data) ? journalResponse.data[0] : journalResponse?.data;

    const {
        data: issuesResponse,
        isLoading: isIssueLoading
    } = useSWR(`/api/issues?journalId=${journal?.id}&isMainSite=${journal?.is_main_site}&withCountDocuments=true`, fetcher, {
        fallbackData: {success: true, data: initialIssuesData},
        revalidateOnMount: false,
        revalidateOnFocus: false,
        revalidateIfStale: false,
        revalidateOnReconnect: false,
    });

    const issuesData = issuesResponse?.data ?? [];

    if (isJournalLoading || isIssueLoading) {
        return (
            <Loading/>
        );
    }

    const archives: Archive[] = (MapToArchiveItems(issuesData) ?? []).filter(
        (archive: Archive) => archive.id.toString() !== journal.default_issue_id?.toString()
    );

    const volumes: Volume[] = MapToVolumeItems(archives) ?? [];
    volumes.sort((a, b) => {
        const numA = parseInt(a.volume_name, 10);
        const numB = parseInt(b.volume_name, 10);
        const isNumA = !isNaN(numA);
        const isNumB = !isNaN(numB);

        if (isNumA && isNumB) {
            return numB - numA;
        } else if (isNumA) {
            return -1;
        } else if (isNumB) {
            return 1;
        } else {
            return a.volume_name.localeCompare(b.volume_name);
        }
    });

    const archiveIssues: ArchiveIssue[] =
        MapToArchiveIssueItems({journal, archives}) ?? [];
    archiveIssues.sort((a, b) => {
        const numA = parseInt(a.name, 10);
        const numB = parseInt(b.name, 10);
        const isNumA = !isNaN(numA);
        const isNumB = !isNaN(numB);

        if (isNumA && isNumB) {
            return numA - numB;
        } else if (isNumA) {
            return -1;
        } else if (isNumB) {
            return 1;
        } else {
            return a.name.localeCompare(b.name);
        }
    });

    return (
        <>
            <Box sx={{width: {lg: "100%", xs: "100%"}}}>
                <Grid container spacing={1} id="archive">
                    <Grid size={{xs: 12, sm: 12, md: 12, lg: 2, xl: 2}} sx={{paddingTop: "1.5rem"}}>
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "center",
                                alignItems: "center",
                                paddingTop: "1.5rem",
                                position: "relative",
                                top: {lg: "115px"},
                                paddingRight: ".5rem",
                                paddingY: "3rem",
                                width: "100%",
                            }}
                        >
                            <JournalCoverImage
                                journalName={journal.title}
                                image={
                                    journal.meta_data?.default_cover_image
                                        ? `${process.env.NEXT_PUBLIC_SERVICE_URI_CDN_SERVE}${journal.meta_data.default_cover_image}`
                                        : null
                                }
                                icon={faFileImage}
                                width={170}
                                height={240}
                            />
                            {journal.meta_data?.default_cover_image && (
                                <SuccessButton
                                    sx={{
                                        marginTop: ".8rem",
                                        borderRadius: ".45rem",
                                        fontWeight: 700,
                                        fontSize: "0.8rem",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 0.3,
                                        width: "160px",
                                    }}
                                    href={
                                        journal.meta_data?.default_cover_image
                                            ? `${process.env.NEXT_PUBLIC_SERVICE_URI_CDN_SERVE}${journal.meta_data.default_cover_image}`
                                            : "#"
                                    }
                                >
                                    <FontAwesomeIcon icon={faImage}/> Download Cover
                                </SuccessButton>
                            )}
                        </Box>
                    </Grid>

                    <Grid size={{xs: 12, sm: 12, md: 12, lg: 8, xl: 8}}>
                        <CustomDivider sx={{marginY: "1.5rem", marginTop: {lg: "3rem"}}}/>
                        <PageContentBox p={3}>
                            <ArchiveSelector volumes={volumes} issues={archiveIssues}/>
                        </PageContentBox>
                    </Grid>

                    <Grid size={{xs: 12, sm: 12, md: 12, lg: 2, xl: 2}}>
                        <Box sx={{position: "relative", top: "4.57rem"}}>
                            <SocialIcons title={`${journal.title} | Archive`} url={`${journal.journal_url}/archive`}/>
                        </Box>
                    </Grid>
                </Grid>
            </Box>
        </>
    );
}
